import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, FormControlLabel, Checkbox } from "@mui/material";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import {
  useLazyGetDDLShipmentPendingListQuery,
  useLazyGetDDLBranchesWithPendingShipmentsQuery,
} from "store/services/shipmentApi";
import { useLazyGetDDLDriverListQuery } from "store/services/driverApi";
import { useLazyGetDDLContractorListQuery } from "store/services/contractorApi";
import { useLazyGetDdlVendorProfileForCollectionQuery } from "store/services/collectionApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useAuth } from "hooks";

const INITIAL_STATE = {
  account: "",
  entity: "",
  sourceBranch: null, // new
  shipmentId: [],
  contractorId: "",
  driverId: "",
  vehicle_id: "",
  loadedBranch: null,
  isActive: true,
  fromVendor: false,
  vendor: null,
};

const FreeloadingForm = ({
  isBusy = false,
  freeloadingDetail = null,
  onSubmit = () => Promise.resolve({}),
  onVendorChange = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity || "",
      account: viewEntity?.entity?.account || "",
    },
  });

  // Watch form fields
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchContractor = useWatch({ control, name: "contractorId" });
  const _watchDriver = useWatch({ control, name: "driverId" });
  const _watchFromVendor = useWatch({ control, name: "fromVendor" });
  const _watchVendor = useWatch({ control, name: "vendor" });
  const _watchShipmentId = useWatch({ control, name: "shipmentId" });
  const _watchSourceBranch = useWatch({ control, name: "sourceBranch" }); // new

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  const [fetchShipments, { data: shipments, isLoading: loadingShipments }] =
    useLazyGetDDLShipmentPendingListQuery();

  const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] =
    useLazyGetDDLDriverListQuery();

  const [fetchContractors, { data: contractors, isLoading: loadingContractors }] =
    useLazyGetDDLContractorListQuery();

  const [fetchVendors, { data: vendorData, isLoading: loadingVendors }] =
    useLazyGetDdlVendorProfileForCollectionQuery();

  const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();

  // new — branches that have pending shipments
  const [
    fetchSourceBranches,
    { data: sourceBranches, isLoading: loadingSourceBranches },
  ] = useLazyGetDDLBranchesWithPendingShipmentsQuery();

  const [vendorsFetched, setVendorsFetched] = useState(false);

  const selectedDrivers = _watchContractor?.driverId || [];
  const selectedVehicles =
    _watchContractor?.vehicle_id || _watchDriver?.assigned_vehicle_ids || [];

  // Notify parent of vendor change
  React.useEffect(() => {
    onVendorChange(_watchVendor);
  }, [_watchVendor, onVendorChange]);

  // Fetch entities based on account
  useEffect(() => {
    if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id) {
      fetchEntities({ account: _watchAccount._id });
    }
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

  // Fetch loaded branches when account + entity selected
  useEffect(() => {
    if (_watchAccount?._id && _watchEntity?._id) {
      fetchBranches({
        account: _watchAccount._id,
        entity: _watchEntity._id,
      });
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchBranches]);

  // Fetch source branches with pending shipments (non-vendor flow only)
  useEffect(() => {
    if (!_watchEntity?._id || _watchFromVendor) return;
    fetchSourceBranches({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
    });
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    _watchFromVendor,
    fetchSourceBranches,
  ]);

  // Fetch contractors and drivers based on account and entity
  useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchContractors({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
    });
    fetchDrivers({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    fetchContractors,
    fetchDrivers,
  ]);

  // Fetch shipments when source branch is selected (non-vendor flow)
  useEffect(() => {
    if (!_watchEntity?._id || !_watchSourceBranch?._id || _watchFromVendor) return;
    fetchShipments({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
      sourceBranch: _watchSourceBranch._id,
    });
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    _watchSourceBranch?._id,
    _watchFromVendor,
    fetchShipments,
  ]);

  // Fetch vendors and shipments for vendor flow
  useEffect(() => {
    if (!_watchEntity?._id) return;
    if (_watchFromVendor && !vendorsFetched) {
      fetchVendors({ account: _watchAccount?._id, entity: _watchEntity?._id });
      setVendorsFetched(true);
    } else if (_watchFromVendor && _watchVendor?._id) {
      fetchVendors({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
        vendorId: _watchVendor?._id,
      });
    }
  }, [
    _watchFromVendor,
    _watchVendor?._id,
    _watchAccount?._id,
    _watchEntity?._id,
    fetchVendors,
    vendorsFetched,
  ]);

  // Reset on account/entity change
  useEffect(() => {
    setVendorsFetched(false);
    setValue("vendor", null);
    setValue("sourceBranch", null); // new
    setValue("shipmentId", []);
  }, [_watchAccount?._id, _watchEntity?._id, setValue]);

  // Reset shipments when source branch changes
  useEffect(() => {
    setValue("shipmentId", []);
  }, [_watchSourceBranch?._id, setValue]);

  // Populate form in edit mode
  useEffect(() => {
    if (freeloadingDetail?.loading) {
      const detailToReset = {
        account: freeloadingDetail.loading.account || "",
        entity: freeloadingDetail.loading.entity || "",
        shipmentId: freeloadingDetail.loading.shipmentId || [],
        contractorId: freeloadingDetail.collection?.contractorId || "",
        driverId: freeloadingDetail.collection?.driverId || "",
        vehicle_id: freeloadingDetail.collection?.vehicle_id || "",
        loadedBranch: freeloadingDetail.loading.loadedBranch || null,
        isActive: freeloadingDetail.loading.isActive ?? true,
      };
      reset(detailToReset);
    }
  }, [freeloadingDetail, reset]);

  const handleOnSubmit = async (data) => {
    const formattedData = {
      account: data?.account?._id,
      entity: data?.entity?._id,
      shipmentId: data?.shipmentId?.map((shipment) => shipment._id) || [],
      contractorId: data?.contractorId?._id,
      driverId: data?.driverId?._id,
      vehicle_id: data?.vehicle_id?._id,
      loadedBranch: data?.loadedBranch?._id || null,
      isActive: data?.isActive,
      vendor: data?.vendor?._id || null,
    };
    await onSubmit(formattedData);
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {/* From Vendor Checkbox */}
          <Grid item xs={12} mt={-3}>
            <Controller
              name="fromVendor"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label="From Vendor"
                />
              )}
            />
          </Grid>

          {(isAccountAdmin || isSystemAdmin) && (
            <>
              <Grid item md={6}>
                <Controller
                  name="account"
                  control={control}
                  rules={{ required: "Please select an account." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      label="Account"
                      required
                      value={field.value || ""}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("entity", "");
                        setValue("sourceBranch", null);
                        setValue("shipmentId", []);
                        setValue("contractorId", "");
                        setValue("driverId", "");
                        setValue("vehicle_id", "");
                        setValue("vendor", null);
                        setValue("fromVendor", false);
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={accounts || []}
                      loading={loadingAccounts}
                      loadingText="Loading Account..."
                      disabled={isAccountAdmin || !!freeloadingDetail}
                      helperText={errors?.account?.message}
                      error={!!errors?.account}
                    />
                  )}
                />
              </Grid>
              <Grid item md={6}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an entity." }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      required
                      label="Entity"
                      value={value || ""}
                      onChange={(_, data) => {
                        onChange(data);
                        setValue("sourceBranch", null);
                        setValue("shipmentId", []);
                        setValue("contractorId", "");
                        setValue("driverId", "");
                        setValue("vehicle_id", "");
                        setValue("vendor", null);
                        setValue("fromVendor", false);
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={entities || []}
                      loading={loadingEntities}
                      loadingText="Loading Entities..."
                      disabled={!!freeloadingDetail}
                      helperText={errors?.entity?.message}
                      error={!!errors?.entity}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {/* Vendor Selection */}
          {_watchFromVendor && (
            <Grid item md={6}>
              <Controller
                name="vendor"
                control={control}
                rules={{ required: "Please select a vendor." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Vendor Profile"
                    required
                    value={value || ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("shipmentId", []);
                    }}
                    getOptionLabel={(option) =>
                      option && `${option?.shopName} - ${option?.ownerName}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={vendorData?.vendors || []}
                    loading={loadingVendors}
                    loadingText="Loading Vendors..."
                    helperText={errors?.vendor?.message}
                    error={!!errors?.vendor}
                    disabled={!!freeloadingDetail}
                  />
                )}
              />
            </Grid>
          )}

          {/* Source Branch — only shown in non-vendor flow */}
          {!_watchFromVendor && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="sourceBranch"
                control={control}
                rules={{ required: "Please select a source branch." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Source Branch"
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("shipmentId", []);
                    }}
                    getOptionLabel={(option) =>
                      option ? `${option.name} - ${option.code}` : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={sourceBranches || []}
                    loading={loadingSourceBranches}
                    loadingText="Loading Branches..."
                    helperText={errors?.sourceBranch?.message}
                    error={!!errors?.sourceBranch}
                    disabled={!!freeloadingDetail}
                  />
                )}
              />
            </Grid>
          )}

          {/* Shipment ID — for non-vendor flow, requires sourceBranch; for vendor flow, requires vendor */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{
                validate: (value) =>
                  value?.length > 0 || "Please select at least one Shipment ID.",
              }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  multiple
                  label="Shipment ID / Reference"
                  required
                  value={value || []}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => {
                    if (!option?.shipmentId) return "";
                    const destCode = option?.destinationBranch?.code?.trim() || "";
                    const label =
                      option.status === "Return"
                        ? `${option.shipmentId} (R)`
                        : option.status === "Warehousing"
                          ? `${option.shipmentId} (WH)`
                          : option.shipmentId;
                    const reference = option?.reference
                      ? ` | ${option.reference}`
                      : "";
                    const charge = option.charge
                      ? ` - Rs. ${option.charge}`
                      : "";
                    return `${label}${reference} - ${destCode}${charge}`;
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={
                    _watchFromVendor && _watchVendor
                      ? vendorData?.shipments || []
                      : shipments || []
                  }
                  loading={loadingShipments || loadingVendors}
                  loadingText="Loading Shipments..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                  // non-vendor: needs sourceBranch; vendor: needs vendor selected
                  disabled={
                    !!freeloadingDetail ||
                    (!_watchFromVendor && !_watchSourceBranch?._id) ||
                    (_watchFromVendor && !_watchVendor?._id)
                  }
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="contractorId"
              control={control}
              rules={{ required: "Please select Contractor ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Contractor Profile"
                  required
                  value={value || ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("driverId", "");
                    setValue("vehicle_id", "");
                  }}
                  getOptionLabel={(option) =>
                    option && `${option?.contractorId} - ${option?.name}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={contractors || []}
                  loading={loadingContractors}
                  loadingText="Loading Contractors..."
                  helperText={errors?.contractorId?.message}
                  error={!!errors?.contractorId}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="driverId"
              control={control}
              rules={{ required: "Please select Driver ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Driver"
                  required
                  value={value || ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("vehicle_id", "");
                  }}
                  getOptionLabel={(option) =>
                    option && `${option?.driverId} - ${option?.name}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={
                    selectedDrivers.length > 0 ? selectedDrivers : drivers || []
                  }
                  loading={loadingDrivers}
                  loadingText="Loading Drivers..."
                  helperText={errors?.driverId?.message}
                  error={!!errors?.driverId}
                  disabled={!_watchContractor}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="vehicle_id"
              control={control}
              rules={{ required: "Please select Vehicle ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Vehicle"
                  required
                  value={value || ""}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option && option.vehicle_id && option.registration_number
                      ? `${option.vehicle_id} - ${option.registration_number}`
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    typeof option === "object" && typeof value === "object"
                      ? option._id === value._id
                      : option === value
                  }
                  options={selectedVehicles}
                  disabled={!_watchDriver}
                  loadingText="Loading Vehicles..."
                  helperText={errors?.vehicle_id?.message}
                  error={!!errors?.vehicle_id}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="loadedBranch"
              control={control}
              rules={{ required: "Please select the loaded branch." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Loaded Branch"
                  required
                  value={value ?? null}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option
                      ? `${option?.name ?? ""} (${option?.code ?? ""})`.trim()
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id || value === null
                  }
                  options={branches || []}
                  loading={loadingBranches}
                  loadingText="Loading Branches..."
                  helperText={errors?.loadedBranch?.message}
                  error={!!errors?.loadedBranch}
                />
              )}
            />
          </Grid>

          {freeloadingDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          {freeloadingDetail ? "Update Freeloading" : "Add Freeloading"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default FreeloadingForm;